// views/Admin/Statistics/components/Charts/BeaconCharts/ConversionRatio/FunnelStaticChart/FunnelStaticChart.tsx
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { Beacon } from '../../../../../../../../Entities';
import Loading from '../../../../../../../../components/common/Loading/Loading';
import styles from './FunnelStaticChart.module.css';

type WeekStats = Record<string, number>;
type Scope = 'all' | 'custom';

const STEPS = [
  { key: 'homepage_visitors', label: 'QR visitors', colorVar: '--c1', stepNum: 1 },
  { key: 'register_visitors', label: 'Register visitors', colorVar: '--c2', stepNum: 2 },
  { key: 'registerers', label: 'Registrations', colorVar: '--c3', stepNum: 3 },
  { key: 'payers', label: 'Payers', colorVar: '--c4', stepNum: 4 },
] as const;

const MIN_HISTORY_WEEKS = 0;
const MAX_HISTORY_WEEKS = 10;
const DEFAULT_HISTORY_WEEKS = 6;
const DEFAULT_CUSTOM_WEEKS = 4;

// Only the free-text "N weeks" field is debounced — the toggle and the
// stepper are discrete clicks, so they should refetch immediately.
const CUSTOM_WEEKS_DEBOUNCE_MS = 450;

// ── Stats helpers ─────────────────────────────────────────────
// The API returns a flat stats object when asked for -1 ("all time"),
// and a { [weekIndex]: stats } map for any weeks >= 0.

function pct(numerator: number, denominator: number): string {
  if (!denominator) return '0%';
  return `${Math.round((numerator / denominator) * 100)}%`;
}

function overallPct(stats: WeekStats): string {
  const visitors = stats.homepage_visitors ?? 0;
  const payers = stats.payers ?? 0;
  if (!visitors) return '0.0%';
  return `${((payers / visitors) * 100).toFixed(1)}%`;
}

function normalizeStats(raw: WeekStats | null | undefined): WeekStats {
  return STEPS.reduce<WeekStats>((stats, step) => {
    stats[step.key] = Number(raw?.[step.key] ?? 0);
    return stats;
  }, {});
}

function toWeeklyArray(response: Record<string, WeekStats> | null | undefined): WeekStats[] {
  return Object.entries(response ?? {})
    .sort(([weekA], [weekB]) => Number(weekA) - Number(weekB))
    .map(([, stats]) => normalizeStats(stats));
}

function aggregateWeeks(weeks: WeekStats[]): WeekStats {
  return weeks.reduce<WeekStats>((totals, week) => {
    STEPS.forEach((step) => {
      totals[step.key] = (totals[step.key] ?? 0) + (week[step.key] ?? 0);
    });
    return totals;
  }, {});
}

function weekLabel(weekIndex: number): string {
  if (weekIndex === 0) return 'This week';
  return `${weekIndex} week${weekIndex === 1 ? '' : 's'} ago`;
}

// ── Presentational pieces ────────────────────────────────────

function RefreshTag() {
  return (
    <span className={styles.refreshTag}>
      <span className={styles.spinner} aria-hidden="true" />
      Updating
    </span>
  );
}

function FunnelRow({ stats }: { stats: WeekStats }) {
  return (
    <>
      <div className={styles.funnelRow}>
        {STEPS.map((step, index) => {
          const value = stats[step.key] ?? 0;
          const previous = index > 0 ? stats[STEPS[index - 1].key] ?? 0 : null;
          const isLast = index === STEPS.length - 1;

          return (
            <div key={step.key} className={styles.cardGroup}>
              {previous !== null && (
                <div className={styles.connector}>
                  <span className={styles.connPct}>{pct(value, previous)}</span>
                  <span className={styles.connArrow}>›</span>
                </div>
              )}
              <div
                className={isLast ? `${styles.card} ${styles.cardLast}` : styles.card}
                style={{ '--step-color': `var(${step.colorVar})` } as CSSProperties}
              >
                <span className={styles.stepBadge} style={{ background: `var(${step.colorVar})` }}>
                  {step.stepNum}
                </span>
                <span className={styles.cardLabel}>{step.label}</span>
                <strong className={styles.cardValue}>{value}</strong>
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.overallConv}>
        <div className={styles.ocLeft}>
          <span className={styles.ocDot} style={{ background: 'var(--c1)' }} />
          <span className={styles.ocLabel}>Visitors</span>
        </div>
        <span className={styles.ocLine} />
        <span className={styles.ocArrow}>›</span>
        <div className={styles.ocRight}>
          <span className={styles.ocDot} style={{ background: 'var(--c4)' }} />
          <span className={styles.ocLabel}>Payers</span>
          <span className={styles.ocSep}>·</span>
          <span className={styles.ocPct}>{overallPct(stats)}</span>
        </div>
      </div>
    </>
  );
}

function WeeklyGrid({ weeks }: { weeks: WeekStats[] }) {
  const maxValues = STEPS.map((step) => Math.max(...weeks.map((week) => week[step.key] ?? 0), 0));

  return (
    <div className={styles.gridScroller}>
      <div className={styles.chartGrid}>
        <div className={styles.gridHeader}>Week</div>
        {STEPS.map((step) => (
          <div key={step.key} className={styles.gridHeader} style={{ color: `var(${step.colorVar})` }}>
            <span className={styles.headerStep}>{step.stepNum}</span>
            {step.label}
          </div>
        ))}

        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className={styles.gridRow}>
            <div className={styles.weekCell}>
              <strong>Week {weekIndex}</strong>
              <span>{weekLabel(weekIndex)}</span>
              <em>{overallPct(week)} overall</em>
            </div>

            {STEPS.map((step, stepIndex) => {
              const value = week[step.key] ?? 0;
              const previous = stepIndex > 0 ? week[STEPS[stepIndex - 1].key] ?? 0 : null;
              const ratio = previous !== null ? pct(value, previous) : null;
              const barWidth = maxValues[stepIndex] ? (value / maxValues[stepIndex]) * 100 : 0;

              return (
                <div key={step.key} className={styles.barCell}>
                  <div className={styles.barTrack}>
                    <div
                      className={styles.bar}
                      style={
                        {
                          '--bar-width': `${barWidth}%`,
                          '--bar-color': `var(${step.colorVar})`,
                        } as CSSProperties
                      }
                    />
                  </div>
                  <span className={styles.barValue}>{value}</span>
                  {ratio && <span className={styles.ratio}>{ratio}</span>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function ScopeToggle({
  scope,
  onScopeChange,
  customWeeksDraft,
  onCustomWeeksDraftChange,
  onCustomWeeksCommit,
}: {
  scope: Scope;
  onScopeChange: (scope: Scope) => void;
  customWeeksDraft: string;
  onCustomWeeksDraftChange: (raw: string) => void;
  onCustomWeeksCommit: (raw: string) => void;
}) {
  return (
    <div className={styles.scopeControl}>
      <div className={styles.segmented} role="tablist" aria-label="Funnel time range">
        <button
          type="button"
          role="tab"
          aria-selected={scope === 'all'}
          className={scope === 'all' ? `${styles.segment} ${styles.segmentActive}` : styles.segment}
          onClick={() => onScopeChange('all')}
        >
          All time
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={scope === 'custom'}
          className={scope === 'custom' ? `${styles.segment} ${styles.segmentActive}` : styles.segment}
          onClick={() => onScopeChange('custom')}
        >
          Last N weeks
        </button>
      </div>
      {scope === 'custom' && (
        <input
          type="number"
          className={styles.scopeInput}
          min={1}
          step={1}
          value={customWeeksDraft}
          onChange={(event) => onCustomWeeksDraftChange(event.target.value)}
          onBlur={(event) => onCustomWeeksCommit(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              onCustomWeeksCommit(event.currentTarget.value);
              event.currentTarget.blur();
            }
          }}
          aria-label="Number of weeks"
        />
      )}
    </div>
  );
}

function WeeksStepper({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  return (
    <div className={styles.stepper} role="group" aria-label="Weeks to show">
      <button
        type="button"
        className={styles.stepperButton}
        onClick={() => onChange(Math.max(MIN_HISTORY_WEEKS, value - 1))}
        disabled={value <= MIN_HISTORY_WEEKS}
        aria-label="Show fewer weeks"
      >
        −
      </button>
      <span className={styles.stepperValue}>{value}</span>
      <button
        type="button"
        className={styles.stepperButton}
        onClick={() => onChange(Math.min(MAX_HISTORY_WEEKS, value + 1))}
        disabled={value >= MAX_HISTORY_WEEKS}
        aria-label="Show more weeks"
      >
        +
      </button>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────
//
// The total-funnel and weekly-grid data are fetched independently, each
// on its own trigger (scope/customWeeks vs. historyWeeks). That keeps a
// change to one control from re-fetching — and dimming — the other
// section. Every control applies immediately; only the free-text "N
// weeks" input is debounced, since it fires on every keystroke otherwise.

export default function FunnelStaticChart() {
  const [scope, setScope] = useState<Scope>('all');
  const [customWeeks, setCustomWeeks] = useState(DEFAULT_CUSTOM_WEEKS);
  const [customWeeksDraft, setCustomWeeksDraft] = useState(String(DEFAULT_CUSTOM_WEEKS));
  const [historyWeeks, setHistoryWeeks] = useState(DEFAULT_HISTORY_WEEKS);

  const [overallStats, setOverallStats] = useState<WeekStats>({});
  const [overallLoading, setOverallLoading] = useState(true);
  const [overallReady, setOverallReady] = useState(false);

  const [weeklyStats, setWeeklyStats] = useState<WeekStats[]>([]);
  const [weeklyLoading, setWeeklyLoading] = useState(true);
  const [weeklyReady, setWeeklyReady] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const commitCustomWeeks = useCallback((raw: string) => {
    const parsed = Math.round(Number(raw));
    const safe = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    setCustomWeeks(safe);
    setCustomWeeksDraft(String(safe));
  }, []);

  const handleCustomWeeksDraftChange = useCallback(
    (raw: string) => {
      setCustomWeeksDraft(raw);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => commitCustomWeeks(raw), CUSTOM_WEEKS_DEBOUNCE_MS);
    },
    [commitCustomWeeks],
  );

  const handleCustomWeeksCommit = useCallback(
    (raw: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      commitCustomWeeks(raw);
    },
    [commitCustomWeeks],
  );

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  // Total funnel: refetches on scope or committed customWeeks.
  useEffect(() => {
    let cancelled = false;
    const weeksParam = scope === 'all' ? -1 : customWeeks;

    setOverallLoading(true);
    (async () => {
      try {
        const response = await Beacon.getConversionStats(weeksParam);
        if (cancelled) return;
        // -1 returns one flat totals object; any other value returns a
        // per-week map, which we sum ourselves to get the combined total.
        setOverallStats(
          weeksParam === -1
            ? normalizeStats(response as WeekStats)
            : aggregateWeeks(toWeeklyArray(response as Record<string, WeekStats>)),
        );
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) {
          setOverallLoading(false);
          setOverallReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [scope, customWeeks]);

  // Weekly grid: refetches on historyWeeks only.
  useEffect(() => {
    let cancelled = false;

    setWeeklyLoading(true);
    (async () => {
      try {
        const response = await Beacon.getConversionStats(historyWeeks);
        if (cancelled) return;
        setWeeklyStats(toWeeklyArray(response as Record<string, WeekStats>));
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) {
          setWeeklyLoading(false);
          setWeeklyReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [historyWeeks]);

  // Full-page loader only for the very first fetch; every refresh after
  // that keeps the previous chart visible with an inline "Updating" tag.
  if (!overallReady || !weeklyReady) return <Loading />;

  return (
    <div className={styles.wrapper}>
      <header className={styles.chartHeader}>
        <h2>Conversion Funnel</h2>
      </header>

      <section className={`${styles.section} ${styles.totalFunnel}`}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeadingGroup}>
            <h3 className={styles.sectionTitle}>
              {scope === 'all' ? 'All time' : `Sum of ${customWeeks} week${customWeeks === 1 ? '' : 's'} until now (including this week)`}
            </h3>
            {overallLoading && <RefreshTag />}
          </div>
          <ScopeToggle
            scope={scope}
            onScopeChange={setScope}
            customWeeksDraft={customWeeksDraft}
            onCustomWeeksDraftChange={handleCustomWeeksDraftChange}
            onCustomWeeksCommit={handleCustomWeeksCommit}
          />
        </div>
        <div className={overallLoading ? `${styles.sectionBody} ${styles.isRefreshing}` : styles.sectionBody}>
          <FunnelRow stats={overallStats} />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeadingGroup}>
            <h3 className={styles.sectionTitle}>Weekly breakdown</h3>
            {weeklyLoading && <RefreshTag />}
          </div>
          <WeeksStepper value={historyWeeks} onChange={setHistoryWeeks} />
        </div>

        {weeklyStats.length === 0 ? (
          <p className={styles.emptyState}>No weekly data to show.</p>
        ) : (
          <div className={weeklyLoading ? `${styles.sectionBody} ${styles.isRefreshing}` : styles.sectionBody}>
            <WeeklyGrid weeks={weeklyStats} />
          </div>
        )}
      </section>
    </div>
  );
}